import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const benefits = [
    {
      icon: 'TrendingUp',
      title: 'Weekly Digital Insights',
      description: 'Latest trends and strategies in digital marketing'
    },
    {
      icon: 'Lightbulb',
      title: 'Exclusive Tips & Tricks',
      description: 'Insider knowledge from our expert team'
    },
    {
      icon: 'Gift',
      title: 'Free Resources',
      description: 'Templates, guides, and tools delivered to your inbox'
    },
    {
      icon: 'Users',
      title: 'Case Study Previews',
      description: 'First access to our latest success stories'
    }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!email) return;

    setIsLoading(true);
    
    // Simulate subscription
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail('');
    }, 1500);
  };

  if (isSubscribed) {
    return (
      <section className="py-20 bg-gradient-to-br from-primary to-accent">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-12">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={40} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">
              Welcome to the Singh Creations Community!
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Thank you for subscribing! Your first weekly insight will arrive in your inbox soon.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="secondary"
                className="bg-white text-primary hover:bg-gray-100"
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={18}
              >
                Join Our Community
              </Button>
              <Button
                variant="ghost"
                className="text-white hover:bg-white/10"
                iconName="ExternalLink"
                iconPosition="right"
                iconSize={18}
              >
                Follow on Social Media
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-primary to-accent">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Stay Ahead of the Digital Curve
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Join 5,000+ business leaders who receive our weekly digital insights. Get the latest strategies, trends, and exclusive resources delivered straight to your inbox.
              </p>

              {/* Benefits Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits?.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <Icon name={benefit?.icon} size={16} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{benefit?.title}</h3>
                      <p className="text-sm text-white/80">{benefit?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Signup Form */}
            <div className="bg-white rounded-xl p-8 brand-shadow">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  Get Weekly Insights
                </h3>
                <p className="text-muted-foreground">
                  No spam, unsubscribe anytime. We respect your privacy.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  required
                />

                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  loading={isLoading}
                  className="bg-conversion hover:bg-conversion/90 text-conversion-foreground"
                  iconName="Send"
                  iconPosition="right"
                  iconSize={18}
                >
                  {isLoading ? 'Subscribing...' : 'Subscribe Now'}
                </Button>
              </form>

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-border">
                <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Shield" size={16} className="text-success" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Mail" size={16} className="text-trust" />
                    <span>Weekly</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="X" size={16} className="text-muted-foreground" />
                    <span>No Spam</span>
                  </div>
                </div>
              </div>

              {/* Social Proof */}
              <div className="mt-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Join <span className="font-semibold text-foreground">47</span> subscribers
                </p>
                <div className="flex items-center justify-center mt-2 space-x-1">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <Icon key={star} name="Star" size={16} className="text-warning fill-current" />
                  ))}
                  <span className="text-sm text-muted-foreground ml-2">4.9/5 rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;